import React, { memo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../../assets/languages/configs';

import CenterLoading from '../CenterLoading/CenterLoading';
import usePurchaseTicket from '../../hooks/usePurchaseTicket';
import {
    GMO_SHOP_ID,
    GOOGLE_MERCHANT_IDENTIFIER,
    GOOGLE_PAYMENT_ENVIRONMENT,
    PAYMENT_TYPE,
} from '../../utils/Constants';
import { calcTotalPrice, hasSelectedNumber, showNotificationDialog } from '../../utils';

import styles from './styles.module.css';
import { SelectNumber, TicketInfo } from 'types';

interface GooglePaymentProps {
    ticketInfo: TicketInfo;
    selectNumber: SelectNumber | null;
    isConfirmChecked?: boolean;
    reservationCombo?: any;
    userForm?: any;
    anonymousFormAnswers?: any;
    onClick?: () => void;
}

const GooglePayment = memo(
    ({
        ticketInfo,
        selectNumber,
        isConfirmChecked = true,
        reservationCombo,
        userForm,
        anonymousFormAnswers,
        onClick = () => {},
    }: GooglePaymentProps) => {
        const google = window.google;
        const ref = useRef<HTMLDivElement>(null);
        const paymentsClientRef = useRef<any>();
        const {
            isLoading,
            purchaseWithCard,
            showOfflineError,
            WarningConfirmModal,
            hasEmailInUserInfo,
            EmailConfirmModal,
        } = usePurchaseTicket();
        const { t } = useTranslation();
        const { language } = i18n;

        const { salesTicketId } = ticketInfo ?? {};

        useEffect(() => {
            if (!google) {
                return;
            }
            try {
                const paymentsClient = new google.payments.api.PaymentsClient({
                    environment: GOOGLE_PAYMENT_ENVIRONMENT,
                });
                const localeDict: Record<string, string> = {
                    ja: 'ja',
                    en: 'en',
                    vi: 'en',
                    th: 'th',
                    ko: 'ko',
                    zh: 'zh',
                    'zh-TW': 'zh',
                };
                const buttonLocale = localeDict?.[language] || 'en';

                const ButtonElement = paymentsClient.createButton({
                    buttonColor: 'black',
                    buttonSizeMode: 'fill',
                    buttonType: 'plain',
                    buttonLocale: buttonLocale,
                    onClick: () => {},
                });

                paymentsClientRef.current = paymentsClient;

                ref.current?.appendChild(ButtonElement);
            } catch (error) {
                console.log(error);
            }
        }, [google, language]);

        const handlePaymentBtnClick = async () => {
            if (!navigator.onLine) {
                showOfflineError();
                return;
            }

            if (!hasSelectedNumber(selectNumber)) {
                showNotificationDialog(
                    'error',
                    'selectNumberError',
                    t('notification.title'),
                    t('validate.minimum-one-person'),
                );
                return;
            }

            if (isConfirmChecked === false) {
                onClick();
                return;
            }

            if (!hasEmailInUserInfo()) {
                return;
            }

            const paymentsClient = paymentsClientRef.current;
            const totalPrice = calcTotalPrice(selectNumber, {
                adult: ticketInfo?.priceAdult ?? 0,
                custom1: ticketInfo?.priceCustom1 ?? 0,
                custom2: ticketInfo?.priceCustom2 ?? 0,
                custom3: ticketInfo?.priceCustom3 ?? 0,
            });
            const paymentDataRequest = {
                apiVersion: 2,
                apiVersionMinor: 0,
                emailRequired: true,
                allowedPaymentMethods: [
                    {
                        type: 'CARD',
                        parameters: {
                            allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
                            allowedCardNetworks: ['AMEX', 'DISCOVER', 'INTERAC', 'JCB', 'MASTERCARD', 'VISA'],
                        },
                        tokenizationSpecification: {
                            type: 'PAYMENT_GATEWAY',
                            parameters: {
                                gateway: 'gmopg',
                                gatewayMerchantId: GMO_SHOP_ID,
                            },
                        },
                    },
                ],
                merchantInfo: {
                    merchantId: GOOGLE_MERCHANT_IDENTIFIER,
                    merchantName: 'QUICK TRIP',
                },
                transactionInfo: {
                    totalPriceStatus: 'FINAL',
                    totalPriceLabel: 'Total',
                    totalPrice: String(totalPrice),
                    currencyCode: 'JPY',
                    countryCode: 'JP',
                },
            };

            try {
                const isReadyToPay = await paymentsClient.isReadyToPay(paymentDataRequest);
                if (!isReadyToPay.result) return;
                const paymentData = await paymentsClient.loadPaymentData(paymentDataRequest);
                const paymentToken = btoa(paymentData.paymentMethodData.tokenizationData.token);
                await purchaseWithCard({
                    salesTicketId: salesTicketId,
                    number: selectNumber,
                    cardToken: paymentToken,
                    paymentType: PAYMENT_TYPE.googlePay,
                    reservationCombo,
                    userForm,
                    email: paymentData.email,
                    anonymousFormAnswers,
                });
            } catch (error) {
                console.error(error);
            }
        };

        if (!google) {
            return <></>;
        }

        return (
            <>
                {isLoading && <CenterLoading overFooter />}
                <WarningConfirmModal />
                <EmailConfirmModal />
                <div onClick={() => void handlePaymentBtnClick()} className={styles.googlePayBtn} ref={ref}></div>
            </>
        );
    },
);

export default GooglePayment;
